/**
 * Reifenrechner: vergleicht zwei Reifennennmasse geometrisch.
 *
 * Die Rechenkerne sind ein Port von wawi
 * `packages/shared/src/calculators/tire-dimension.ts` (dort mit sechs Tests
 * belegt). Bewusst als eigenstaendige Kopie in Vanilla-JS statt als Import:
 * das Theme hat keinen Bundler, und die Formeln sind vier Zeilen. Wer eine
 * davon aendert, aendert sie in wawi mit.
 *
 * WICHTIG zur Benennung: Was hier gerechnet wird, ist der NENN-Aussenumfang
 * aus Durchmesser mal Pi. Das ist nicht der dynamische Abrollumfang, den ein
 * Reifen unter Last und Luftdruck tatsaechlich abrollt (der liegt einige
 * Prozent darunter). Fuer den VERGLEICH zweier Groessen ist das egal, weil
 * sich der Faktor herauskuerzt, fuer eine absolute Angabe waere es falsch.
 * Deshalb wird der Umfang nur relativ ausgegeben, nie als Absolutwert mit
 * dem Etikett "Abrollumfang". Gegenprobe 31.08.2026 gegen veroeffentlichte
 * ETRTO-Normwerte (421 Groessen): Abrollumfang/Nennumfang 0,971, das
 * Verhaeltnis zweier Groessen weicht < 0,2 Prozentpunkte ab. Die Normwerte
 * selbst werden auf Owner-Entscheidung NICHT ausgeliefert.
 *
 * Die Tachoabweichung ist reine Physik, keine Rechtsaussage: ein groesserer
 * Reifen legt je Radumdrehung mehr Weg zurueck, der auf die alte Groesse
 * kalibrierte Tacho zeigt entsprechend zu wenig an. Was zulaessig ist, steht
 * in den Fahrzeugpapieren und in der Freigabe, nicht in diesem Rechner.
 */
(function () {
  'use strict';

  var ZOLL_IN_MM = 25.4;
  var GRENZEN = {
    breite: { min: 100, max: 500 },
    quer: { min: 20, max: 90 },
    zoll: { min: 10, max: 30 },
  };

  /* ----------------------------------------------------------- Rechenkern */

  function istGueltig(g) {
    return (
      inSpanne(g.breite, GRENZEN.breite) &&
      inSpanne(g.quer, GRENZEN.quer) &&
      inSpanne(g.zoll, GRENZEN.zoll)
    );
  }

  function inSpanne(wert, s) {
    return Number.isFinite(wert) && wert >= s.min && wert <= s.max;
  }

  /** Flankenhoehe, Nenndurchmesser und Nenn-Aussenumfang in mm. */
  function masse(g) {
    var flanke = (g.breite * g.quer) / 100;
    var durchmesser = g.zoll * ZOLL_IN_MM + 2 * flanke;
    return {
      flanke: flanke,
      durchmesser: durchmesser,
      umfang: durchmesser * Math.PI,
    };
  }

  function vergleich(alt, neu) {
    var a = masse(alt);
    var b = masse(neu);
    return {
      alt: a,
      neu: b,
      abweichungProzent: ((b.durchmesser - a.durchmesser) / a.durchmesser) * 100,
      hoeheMm: (b.durchmesser - a.durchmesser) / 2,
      umfangVerhaeltnis: 100 * (b.umfang / a.umfang),
    };
  }

  /**
   * Groesse aus Freitext lesen. Absichtlich grosszuegiger als die Vorlage in
   * wawi: Kunden tippen "255/55 R18", "255/55R18", "255 55 18" und
   * "255/55 ZR18". Die Zollzahl darf einstellig sein (Anhaenger), die Breite
   * dreistellig bleiben.
   */
  function ausText(text) {
    var t = String(text || '').trim();
    var m = /^(\d{2,3})\s*[\/\s-]\s*(\d{2})\s*(?:[A-Z]{0,2}R|R|\s)?\s*(\d{1,2})(?:\D.*)?$/i.exec(t);
    if (!m) return null;
    var g = { breite: +m[1], quer: +m[2], zoll: +m[3] };
    return istGueltig(g) ? g : null;
  }

  function alsText(g) {
    return g.breite + '/' + g.quer + ' R' + g.zoll;
  }

  /* -------------------------------------------------------------- Bewertung */

  /**
   * Einordnung ausschliesslich nach der gemessenen Groessenabweichung, ohne
   * Rechtsaussage. Die Schwellen trennen "merkt man nicht" von "merkt man"
   * von "deutlich" und sind bewusst als Hinweis formuliert. Verbindlich
   * bleibt, was in den Fahrzeugpapieren steht.
   */
  function einordnen(prozent) {
    var b = Math.abs(prozent);
    if (b <= 1) {
      return {
        stufe: 'gut',
        titel: 'Nahezu gleicher Abrollradius',
        text: 'Die beiden Größen liegen unter einem Prozent auseinander. Tacho und Bodenfreiheit ändern sich praktisch nicht. Ob die Größe an Ihrem Fahrzeug zulässig ist, klären Fahrzeugpapiere, Freigabe des Rades und Prüfstelle.',
      };
    }
    if (b <= 2.5) {
      return {
        stufe: 'mittel',
        titel: 'Spürbarer, aber kleiner Unterschied',
        text: 'Der Unterschied liegt im Bereich, in dem sich Tachoanzeige und Bodenfreiheit messbar verändern. Ob die Größe an Ihrem Fahrzeug zulässig ist, klären Sie vor dem Kauf mit der Prüfstelle und anhand Ihrer Fahrzeugpapiere.',
      };
    }
    return {
      stufe: 'hoch',
      titel: 'Deutlicher Unterschied',
      text: 'Mehr als zweieinhalb Prozent Abweichung verändern Tachoanzeige, Bodenfreiheit und den Platz im Radhaus sichtbar. Diese Größe gehört vor Kauf und Montage mit der Prüfstelle abgestimmt und gegen Fahrzeugpapiere und Freigabe geprüft.',
    };
  }

  /* ------------------------------------------------------------- Darstellung */

  function zahl(wert, stellen) {
    return wert.toLocaleString('de-DE', {
      minimumFractionDigits: stellen,
      maximumFractionDigits: stellen,
    });
  }

  function mitVorzeichen(wert, stellen) {
    return (wert > 0 ? '+' : wert < 0 ? '−' : '') + zahl(Math.abs(wert), stellen);
  }

  function suchLink(g) {
    return '/search?q=' + encodeURIComponent(g.breite + '/' + g.quer + ' R' + g.zoll) + '&type=product';
  }

  /* ------------------------------------------------------------------ Widget */

  function init(wurzel) {
    var felder = {
      altBreite: wurzel.querySelector('[data-rr="alt-breite"]'),
      altQuer: wurzel.querySelector('[data-rr="alt-quer"]'),
      altZoll: wurzel.querySelector('[data-rr="alt-zoll"]'),
      neuBreite: wurzel.querySelector('[data-rr="neu-breite"]'),
      neuQuer: wurzel.querySelector('[data-rr="neu-quer"]'),
      neuZoll: wurzel.querySelector('[data-rr="neu-zoll"]'),
    };
    var ausgabe = wurzel.querySelector('[data-rr="ausgabe"]');
    var hinweis = wurzel.querySelector('[data-rr="hinweis"]');
    if (!ausgabe) return;

    function lies(praefix) {
      var g = {
        breite: parseFloat(felder[praefix + 'Breite'].value),
        quer: parseFloat(felder[praefix + 'Quer'].value),
        zoll: parseFloat(felder[praefix + 'Zoll'].value),
      };
      return istGueltig(g) ? g : null;
    }

    function schreib(praefix, g) {
      felder[praefix + 'Breite'].value = g.breite;
      felder[praefix + 'Quer'].value = g.quer;
      felder[praefix + 'Zoll'].value = g.zoll;
    }

    function rechne() {
      var alt = lies('alt');
      var neu = lies('neu');

      if (!alt || !neu) {
        ausgabe.hidden = true;
        if (hinweis) {
          hinweis.hidden = false;
          hinweis.textContent =
            'Bitte beide Größen vollständig eintragen. Breite ' +
            GRENZEN.breite.min + ' bis ' + GRENZEN.breite.max + ' mm, Querschnitt ' +
            GRENZEN.quer.min + ' bis ' + GRENZEN.quer.max + ', Felge ' +
            GRENZEN.zoll.min + ' bis ' + GRENZEN.zoll.max + ' Zoll.';
        }
        return;
      }
      if (hinweis) hinweis.hidden = true;

      var v = vergleich(alt, neu);
      var urteil = einordnen(v.abweichungProzent);

      setzen('urteil-titel', urteil.titel);
      setzen('urteil-text', urteil.text);
      var karte = wurzel.querySelector('[data-rr="urteil"]');
      if (karte) karte.setAttribute('data-stufe', urteil.stufe);

      setzen('wert-abweichung', mitVorzeichen(v.abweichungProzent, 1) + ' %');
      setzen('wert-durchmesser-alt', zahl(v.alt.durchmesser, 0) + ' mm');
      setzen('wert-durchmesser-neu', zahl(v.neu.durchmesser, 0) + ' mm');
      setzen('wert-flanke-alt', zahl(v.alt.flanke, 0) + ' mm');
      setzen('wert-flanke-neu', zahl(v.neu.flanke, 0) + ' mm');
      setzen('wert-hoehe', mitVorzeichen(v.hoeheMm, 1) + ' mm');

      // Tacho: der auf die alte Groesse kalibrierte Zeiger zeigt 100 km/h,
      // gefahren werden 100 mal dem Umfangsverhaeltnis. Das ist eine
      // RELATIVE Aussage (Kalibrierung wie vor dem Wechsel). Ob der Tacho
      // danach absolut zu wenig anzeigt, haengt von der werksseitigen
      // Voreilung ab; UNECE R39 verlangt: nie weniger als die echte
      // Geschwindigkeit, hoechstens 10 % + 4 km/h mehr. Deshalb keine
      // absolute Formulierung im Text.
      var echte100 = 100 * (v.neu.umfang / v.alt.umfang);
      setzen('wert-tacho', zahl(echte100, 1) + ' km/h');
      setzen(
        'wert-tacho-zusatz',
        echte100 > 100
          ? 'Gegenüber vorher zeigt der Tacho bei gleicher Geschwindigkeit weniger an.'
          : echte100 < 100
            ? 'Gegenüber vorher zeigt der Tacho bei gleicher Geschwindigkeit mehr an.'
            : 'Die Anzeige bleibt unverändert.'
      );

      setzen('wert-groesse-alt', alsText(alt));
      setzen('wert-groesse-neu', alsText(neu));

      var link = wurzel.querySelector('[data-rr="link-reifen"]');
      if (link) {
        link.href = suchLink(neu);
        link.textContent = 'Reifen in ' + alsText(neu) + ' ansehen';
      }
      // Zweites Ziel sind die Felgen im neuen Zoll. Die Groessenkollektionen
      // gibt es real nur von 15 bis 23 Zoll (am 31.08. einzeln auf HTTP 200
      // geprueft); ausserhalb dieser Spanne wuerde ein konstruierter Handle
      // in einen 404 laufen. /collections/felgen gibt es NICHT, die Sammelseite
      // ist /pages/felgensortiment.
      var linkFe = wurzel.querySelector('[data-rr="link-felgen"]');
      if (linkFe) {
        var hatSeite = neu.zoll >= 15 && neu.zoll <= 23;
        linkFe.href = hatSeite ? '/collections/' + neu.zoll + '-zoll-felgen' : '/pages/felgensortiment';
        linkFe.textContent = hatSeite
          ? 'Felgen in ' + neu.zoll + ' Zoll ansehen'
          : 'Alle Felgen ansehen';
      }

      ausgabe.hidden = false;
    }

    function setzen(name, text) {
      var el = wurzel.querySelector('[data-rr="' + name + '"]');
      if (el) el.textContent = text;
    }

    Object.keys(felder).forEach(function (k) {
      if (felder[k]) felder[k].addEventListener('input', rechne);
    });

    // Beispielgroessen: ein Klick fuellt beide Seiten. Die Werte stehen im
    // Markup (data-alt / data-neu), damit sie ohne JS-Aenderung pflegbar sind.
    wurzel.querySelectorAll('[data-rr-beispiel]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var a = ausText(btn.getAttribute('data-alt'));
        var n = ausText(btn.getAttribute('data-neu'));
        if (!a || !n) return;
        schreib('alt', a);
        schreib('neu', n);
        rechne();
        ausgabe.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });

    rechne();
  }

  function start() {
    document.querySelectorAll('[data-zr-reifenrechner]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Fuer Tests und die Vorschau ausserhalb des Themes.
  window.zrReifenrechner = { masse: masse, vergleich: vergleich, ausText: ausText, einordnen: einordnen };
})();
